import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Button } from '@progress/kendo-react-buttons';
import { useNavigate, useParams } from 'react-router-dom';

import { GameStatus } from '../types/state';
import { useGame } from '../hooks/use-game';
import { GameContext } from '../contexts/game';
import { PlayerContext } from '../contexts/player';
import { OverlayContext } from '../contexts/overlay';
import { GameDocumentContext } from '../contexts/game-document';
import { UpdatePlayerStateAsync } from '../utils/game-engine/base';
import { DisplayLanguageContext } from '../contexts/display-languages';
import { generateTitleById } from '../utils/game-document/display-languages';

export const EnterGameButton: React.FC = () => {
  const { gameCode } = useParams();
  const navigate = useNavigate();

  const [game] = React.useContext(GameContext);
  const [, setOverlay] = React.useContext(OverlayContext);
  const [player, setPlayer] = React.useContext(PlayerContext);
  const [gameDocument] = React.useContext(GameDocumentContext);
  const [displayLanguageContext] = React.useContext(DisplayLanguageContext);

  const { data } = useGame(gameCode!);

  const gameStatus = ((game.gameState?.status === 'PreGame'
    ? data?.gameStatus
    : game.gameState?.status) || 'PreGame') as GameStatus;
  const playerStatus = player.playerState?.status || 'waiting';

  const handleEnterGame = () => {
    setOverlay((prevState) => ({
      ...prevState,
      activeTab: 'info',
      drawerIsOpen: false
    }));

    const now = new Date();
    const offsetMinutes = now.getTimezoneOffset();
    const adjustedTime = new Date(now.getTime() - offsetMinutes * 60 * 1000);

    setPlayer((prev) =>
      UpdatePlayerStateAsync(prev, {
        ...prev.playerState!,
        status: 'playing',
        joinedDate: !prev?.playerState?.joinedDate
          ? adjustedTime.toISOString()
          : prev?.playerState?.joinedDate
      })
    );

    navigate(`/games/${gameCode}/map`);
  };

  const isDisableEnterGame = React.useMemo(() => {
    const isTeamEmpty = !Boolean(player.playerState?.teamCode);
    const mustJoinTeam =
      gameDocument.gameDocument?.settings.preGame.isPreGameTeamRequired &&
      isTeamEmpty;

    return (
      ['Paused', 'PreGame', 'Finished'].includes(gameStatus) ||
      ['playing', 'kicked', 'finished'].includes(playerStatus) ||
      mustJoinTeam
    );
  }, [
    gameStatus,
    playerStatus,
    player.playerState?.teamCode,
    gameDocument.gameDocument?.settings.preGame.isPreGameTeamRequired
  ]);

  return (
    <Container className={'drawer__footer p-0 text-center py-2'} fluid>
      {['waiting', 'ready'].includes(playerStatus) && (
        <Row>
          <Col>
            <Button
              className={'w-100'}
              themeColor={'success'}
              size={'large'}
              onClick={handleEnterGame}
              disabled={isDisableEnterGame}>
              {generateTitleById(
                'e4d66a9d-ed75-443e-82f1-9a8da53325d8',
                gameDocument,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'Enter game'}
            </Button>
          </Col>
        </Row>
      )}
    </Container>
  );
};
